import { useEffect, useRef } from 'react';

/** Inputs that take no typing: after clicking a tab or a box, shortcuts still work. */
const NOT_TYPED = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
]);

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !NOT_TYPED.has(target.type);
  return target.isContentEditable || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

/**
 * Single-key shortcuts ("n", "1"…), ignored while typing or with modifier keys, and while a
 * dialog is open.
 */
export function useShortcuts(bindings: Record<string, () => void>): void {
  const latest = useRef(bindings);
  latest.current = bindings;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      if (isTyping(event.target) || document.querySelector('dialog[open]')) return;
      // Space and Enter already activate a focused button or link.
      const target = event.target as HTMLElement | null;
      if ((event.key === ' ' || event.key === 'Enter') && target?.closest('button, a')) return;
      const action = latest.current[event.key];
      if (!action) return;
      event.preventDefault();
      action();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
